import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import LoaderSpiner from "../../Components/LoaderSpiner";
import styles from "./BalanceStatement.module.css";

const BalanceStatement = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [balanceData, setBalanceData] = useState({
    balance: 0,
    totalRecharges: 0,
    totalRechargedAmount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(null);
  const [dateRange, setDateRange] = useState({ startDate: "", endDate: "" });

  const token = localStorage.getItem("token");

  // Extract date from campaign name
  const extractDateFromCampaignName = (campaignName) => {
    if (!campaignName) return null;
    const parts = campaignName.split(" - ");
    if (parts.length < 3) {
      console.warn(`Invalid campaign name format: ${campaignName}`);
      return null;
    }
    try {
      return new Date(parts[1]); // Assuming date is in format MM/DD/YYYY
    } catch {
      console.warn(`Invalid date format in campaign name: ${campaignName}`);
      return null;
    }
  };

  // Fetch campaigns, participants, and balance data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const campaignResponse = await fetch(
          `https://prod.toxboxai.com/api/campaign/getallcampaign?_t=${Date.now()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!campaignResponse.ok)
          throw new Error("Failed to fetch campaign data");
        const campaignData = await campaignResponse.json();
        if (!campaignData.success)
          throw new Error("Campaign API response unsuccessful");

        const participantResponse = await fetch(
          `https://prod.toxboxai.com/api/vendor/participants`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!participantResponse.ok)
          throw new Error("Failed to fetch participant data");
        const participantData = await participantResponse.json();
        if (!participantData.success)
          throw new Error("Participant API response unsuccessful");

        const balanceResponse = await fetch(
          `https://prod.toxboxai.com/api/recharge/balance?_t=${Date.now()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!balanceResponse.ok)
          throw new Error("Failed to fetch balance data");
        const balanceData = await balanceResponse.json();
        if (!balanceData.success)
          throw new Error("Balance API response unsuccessful");

        console.log("Campaign Data:", campaignData.data.campaigns);
        console.log("Participant Data:", participantData.data);
        console.log("Balance Data:", balanceData.data);

        campaignData.data.campaigns.forEach((campaign) => {
          console.log(
            `Campaign ${campaign.campaignname} Assignment History:`,
            campaign.creditAssignmentHistory
          );
          console.log(
            `Campaign ${campaign.campaignname} Usage History:`,
            campaign.creditUsageHistory
          );
        });

        setCampaigns(campaignData.data.campaigns || []);
        setFilteredCampaigns(campaignData.data.campaigns || []);
        setParticipants(participantData.data || []);
        setBalanceData(
          balanceData.data || {
            balance: 0,
            totalRecharges: 0,
            totalRechargedAmount: 0,
          }
        );
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter campaigns by date range
  useEffect(() => {
    const filterCampaigns = () => {
      if (!dateRange.startDate || !dateRange.endDate) {
        setFilteredCampaigns(campaigns);
        return;
      }

      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      end.setHours(23, 59, 59, 999); // Include entire end date

      const filtered = campaigns.filter((campaign) => {
        const campaignDate = extractDateFromCampaignName(campaign.campaignname);
        return campaignDate && campaignDate >= start && campaignDate <= end;
      });

      setFilteredCampaigns(filtered);
    };

    filterCampaigns();
  }, [campaigns, dateRange]);

  // Calculate summary metrics for filtered campaigns
  const totalCampaigns = filteredCampaigns.length;
  const totalCreditAssigned = filteredCampaigns
    .reduce(
      (sum, campaign) =>
        sum +
        (campaign.creditAssignmentHistory || []).reduce(
          (acc, entry) => acc + (entry.amount || 0),
          0
        ),
      0
    )
    .toFixed(2);
  const totalCreditUsed = filteredCampaigns
    .reduce(
      (sum, campaign) =>
        sum +
        (campaign.creditUsageHistory || []).reduce(
          (acc, entry) => acc + (entry.amountUsed || 0),
          0
        ),
      0
    )
    .toFixed(2);
  const totalCreditBalance = filteredCampaigns
    .reduce((sum, campaign) => sum + (campaign.creditBalance || 0), 0)
    .toFixed(2);

  // Format date for display and export
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "-";
    }
  };

  // Get participant name by ID
  const getParticipantName = (participantId) => {
    if (!participantId) return "-";
    const participant = participants.find((p) => p._id === participantId);
    return participant ? participant.name : participantId;
  };

  // Toggle popup for a specific campaign
  const togglePopup = (campaignId) => {
    setShowPopup(showPopup === campaignId ? null : campaignId);
  };

  // Handle date range change
  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({ ...prev, [name]: value }));
  };

  const exportAllToExcel = () => {
    const wb = XLSX.utils.book_new();

    const allData = [
      ["Vendor Summary"],
      ["Metric", "Value"],
      [
        "Current Balance",
        balanceData.balance ? balanceData.balance.toFixed(2) : "0.00",
      ],
      ["Total Recharges", balanceData.totalRecharges || 0],
      [
        "Total Recharged Amount",
        balanceData.totalRechargedAmount
          ? balanceData.totalRechargedAmount.toFixed(2)
          : "0.00",
      ],
      ["Total Campaigns", totalCampaigns],
      ["Total Credit Used By Sales Person", totalCreditUsed],
      ["Total Assigned Balance", totalCreditBalance],
      [], // Empty row for separation
      ["Credit Assignments"],
      ["Campaign Name", "Date", "Amount", "Notes"],
      ...filteredCampaigns.flatMap((campaign) => {
        if (
          !campaign.creditAssignmentHistory ||
          !campaign.creditAssignmentHistory.length
        ) {
          console.warn(
            `No credit assignment history for campaign: ${campaign.campaignname || "Unknown"
            }`
          );
          return [];
        }
        return campaign.creditAssignmentHistory.map((entry) => {
          if (!entry._id) {
            console.warn(
              `Invalid assignment entry in ${campaign.campaignname}:`,
              entry
            );
          }
          return [
            campaign.campaignname || "-",
            formatDate(entry.date),
            (entry.amount || 0).toFixed(2),
            entry.notes || "-",
          ];
        });
      }),
      [], // Empty row for separation
      ["Credit Usage"],
      ["Campaign Name", "Date", "Amount Used", "Description", "Used By"],
      ...filteredCampaigns.flatMap((campaign) => {
        if (
          !campaign.creditUsageHistory ||
          !campaign.creditUsageHistory.length
        ) {
          console.warn(
            `No credit usage history for campaign: ${campaign.campaignname || "Unknown"
            }`
          );
          return [];
        }
        return campaign.creditUsageHistory.map((entry) => {
          if (!entry._id) {
            console.warn(
              `Invalid usage entry in ${campaign.campaignname}:`,
              entry
            );
          }
          return [
            campaign.campaignname || "-",
            formatDate(entry.date),
            (entry.amountUsed || 0).toFixed(2),
            entry.description || "-",
            getParticipantName(entry.participantId),
          ];
        });
      }),
    ];

    console.log("All Data:", allData);

    const allSheet = XLSX.utils.aoa_to_sheet(
      allData.length > 1 ? allData : [["No Data", "No data available"]]
    );
    XLSX.utils.book_append_sheet(wb, allSheet, "Balance Statement");

    try {
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], { type: "application/octet-stream" });
      saveAs(blob, "BalanceStatementAll.xlsx");
    } catch (err) {
      console.error("Error writing all data Excel file:", err);
      alert(
        "Failed to export all data Excel file. Please check the console for details."
      );
    }
  };

  if (loading) {
    return <LoaderSpiner />;
  }

  if (error) {
    return (
      <div className={styles.container}>
        <p className={styles.textMuted}>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Balance Statement</h1>

      {/* Date Range Filter */}
      <div className={styles.dateRangeFilter}>
        <h3 className={styles.sectionTitle}>Filter by Date Range</h3>
        <div className={styles.dateInputs}>
          <label>
            Start Date:
            <input
              type="date"
              name="startDate"
              value={dateRange.startDate}
              onChange={handleDateRangeChange}
              className={styles.dateInput}
            />
          </label>
          <label>
            End Date:
            <input
              type="date"
              name="endDate"
              value={dateRange.endDate}
              onChange={handleDateRangeChange}
              className={styles.dateInput}
            />
          </label>
        </div>
      </div>

      {/* Summary Section */}
      <div className={styles.summaryCard}>
        <button
          onClick={exportAllToExcel}
          className={styles.exportButton}
          disabled={loading}
        >
          Export All to Excel
        </button>

        <h2 className={styles.sectionTitle}>Balance Summary for Vendor</h2>
        <div className={styles.summaryGrid}>
          <div className={`${styles.summaryItem} ${styles.bgBlue}`}>
            <p>Current Balance</p>
            <p>
              {balanceData.balance ? balanceData.balance.toFixed(2) : "0.00"}
            </p>
          </div>
          <div className={`${styles.summaryItem} ${styles.bgGreen}`}>
            <p>Total Recharges</p>
            <p>{balanceData.totalRecharges || 0}</p>
          </div>
          <div className={`${styles.summaryItem} ${styles.bgYellow}`}>
            <p>Total Recharged Amount</p>
            <p>
              {balanceData.totalRechargedAmount
                ? balanceData.totalRechargedAmount.toFixed(2)
                : "0.00"}
            </p>
          </div>
        </div>

        <h2 className={styles.sectionTitle}>Balance Summary for Campaigns</h2>
        <div className={styles.summaryGrid}>
          <div className={`${styles.summaryItem} ${styles.bgBlue}`}>
            <p>Total Campaigns</p>
            <p>{totalCampaigns}</p>
          </div>
          <div className={`${styles.summaryItem} ${styles.bgYellow}`}>
            <p>Total Credit Used By Sales Person</p>
            <p>{totalCreditUsed}</p>
          </div>
          <div className={`${styles.summaryItem} ${styles.bgPurple}`}>
            <p>Total Assigned Balance</p>
            <p>{totalCreditBalance}</p>
          </div>
        </div>
      </div>

      {/* Campaign Details */}
      <div className={styles.campaignList}>
        {filteredCampaigns.map((campaign) => (
          <div key={campaign._id} className={styles.campaignCard}>
            <h3 className={styles.sectionTitle}>
              {campaign.campaignname || "Unknown Campaign"}
            </h3>
            <p className={styles.textMuted}>
              Credit Balance: {(campaign.creditBalance || 0).toFixed(2)}
            </p>

            <h4 className={styles.subSectionTitle}>
              Credit Assignment History
            </h4>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {campaign.creditAssignmentHistory &&
                  campaign.creditAssignmentHistory.length > 0 ? (
                  campaign.creditAssignmentHistory.map((entry) => (
                    <tr key={entry._id || Math.random()}>
                      <td>{formatDate(entry.date)}</td>
                      <td>{(entry.amount || 0).toFixed(2)}</td>
                      <td>{entry.notes || "-"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className={styles.textMuted}>
                      No credit assignment history available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <h4 className={styles.subSectionTitle}>Credit Usage History</h4>
            {campaign.creditUsageHistory &&
              campaign.creditUsageHistory.length > 0 ? (
              <>
                <button
                  onClick={() => togglePopup(campaign._id)}
                  className={styles.viewButton}
                >
                  View Credit Usage History
                </button>

                {showPopup === campaign._id && (
                  <div className={styles.popupOverlay}>
                    <div className={styles.popupContent}>
                      <h4 className={styles.sectionTitle}>
                        Credit Usage History for{" "}
                        {campaign.campaignname || "Unknown Campaign"}
                      </h4>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Amount Used</th>
                            <th>Description</th>
                            <th>Used By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {campaign.creditUsageHistory.map((entry) => (
                            <tr key={entry._id || Math.random()}>
                              <td>{formatDate(entry.date)}</td>
                              <td>{(entry.amountUsed || 0).toFixed(2)}</td>
                              <td>{entry.description || "-"}</td>
                              <td>{getParticipantName(entry.participantId)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <button
                        onClick={() => togglePopup(campaign._id)}
                        className={styles.closeButton}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className={styles.emptyState}>No credit usage recorded.</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BalanceStatement;
